import React, { useState } from 'react';
import { Video, Users, Calendar, Phone } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PeerPracticeSection = () => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [userLevel, setUserLevel] = useState('intermediate');
  const [showHostModal, setShowHostModal] = useState(false);
  const [isInSession, setIsInSession] = useState(false);
  const [sessionType, setSessionType] = useState('jitsi');

  // Mock video conferencing integration
  const startVideoSession = (type = 'jitsi') => {
    setIsInSession(true);
    
    if (type === 'jitsi') {
      // Open Jitsi Meet session
      const roomName = `selenium-interview-${Date.now()}`;
      const jitsiUrl = `https://meet.jit.si/${roomName}`;
      const newWindow = window.open(jitsiUrl, '_blank', 'width=1200,height=800');
      
      // Monitor window closure
      const checkClosed = setInterval(() => {
        if (newWindow?.closed) {
          setIsInSession(false);
          clearInterval(checkClosed);
          alert('Video session ended. Thank you for practicing!');
        }
      }, 1000);
    }
  };

  const availableSessions = [
    {
      id: 1,
      title: "Automation Framework Discussion",
      host: "Alex Chen",
      participants: 3,
      maxParticipants: 6,
      level: "Intermediate",
      duration: "45 min",
      scheduledTime: "Today 3:00 PM EST",
      topics: ["Selenium", "TestNG", "Page Object Model"],
      hostRating: 4.8,
      sessionType: "group"
    },
    {
      id: 2,
      title: "Mock Interview - Senior QA Role",
      host: "Priya Sharma",
      participants: 1,
      maxParticipants: 2,
      level: "Advanced",
      duration: "60 min",
      scheduledTime: "Tomorrow 10:00 AM EST",
      topics: ["Leadership", "Strategy", "Team Management"],
      hostRating: 4.9,
      sessionType: "interview"
    },
    {
      id: 3,
      title: "API Testing Best Practices",
      host: "Marcus Williams",
      participants: 4,
      maxParticipants: 8,
      level: "Beginner",
      duration: "30 min",
      scheduledTime: "Today 7:00 PM EST",
      topics: ["REST API", "Postman", "Testing Strategies"],
      hostRating: 4.7,
      sessionType: "workshop"
    }
  ];

  const HostSessionModal = () => {
    const [formData, setFormData] = useState({
      title: '',
      level: 'intermediate',
      duration: 60,
      maxParticipants: 6,
      description: '',
      topics: '',
      sessionType: 'group'
    });

    const handleSubmit = (e) => {
      e?.preventDefault();
      alert('Session created successfully! Participants will be notified.');
      setShowHostModal(false);
      setFormData({
        title: '', level: 'intermediate', duration: 60,
        maxParticipants: 6, description: '', topics: '', sessionType: 'group'
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Host a Practice Session</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Session Title</label>
              <input
                type="text"
                required
                value={formData?.title}
                onChange={(e) => setFormData({...formData, title: e?.target?.value})}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., Selenium WebDriver Deep Dive"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Level</label>
                <select
                  value={formData?.level}
                  onChange={(e) => setFormData({...formData, level: e?.target?.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Duration (min)</label>
                <select
                  value={formData?.duration}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e?.target?.value)})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Session Type</label>
              <select
                value={formData?.sessionType}
                onChange={(e) => setFormData({...formData, sessionType: e?.target?.value})}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="group">Group Discussion</option>
                <option value="interview">Mock Interview</option>
                <option value="workshop">Workshop</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Max Participants</label>
              <input
                type="number"
                min="2"
                max="10"
                value={formData?.maxParticipants}
                onChange={(e) => setFormData({...formData, maxParticipants: parseInt(e?.target?.value)})}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Topics (comma separated)</label>
              <input
                type="text"
                value={formData?.topics}
                onChange={(e) => setFormData({...formData, topics: e?.target?.value})}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Selenium, Java, TestNG"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                rows="3"
                value={formData?.description}
                onChange={(e) => setFormData({...formData, description: e?.target?.value})}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Brief description of what you'll cover..."
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <Button type="submit" className="flex-1">Create Session</Button>
              <Button type="button" variant="outline" onClick={() => setShowHostModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const communityStats = {
    activePeers: 347,
    totalSessions: 1283,
    averageRating: 4.7,
    successfulMatches: 2156
  };

  const upcomingBookings = [
    {
      id: 1,
      title: "Java Automation Q&A",
      date: "Jan 8, 2025",
      time: "2:00 PM EST",
      host: "David Kim",
      type: "group",
      status: "confirmed"
    }
  ];

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'text-green-700 bg-green-100 border border-green-300';
      case 'intermediate': return 'text-blue-700 bg-blue-100 border border-blue-300';
      case 'advanced': return 'text-purple-700 bg-purple-100 border border-purple-300';
      default: return 'text-gray-700 bg-gray-100 border border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100 border border-green-300';
      case 'starting-soon': return 'text-orange-700 bg-orange-100 border border-orange-300';
      case 'waiting': return 'text-blue-700 bg-blue-100 border border-blue-300';
      default: return 'text-gray-700 bg-gray-100 border border-gray-300';
    }
  };

  const getSessionTypeIcon = (type) => {
    switch (type) {
      case 'group': return '👥';
      case 'interview': return '🎤';
      case 'workshop': return '🛠️';
      default: return '💬';
    }
  };

  const joinSession = (sessionId) => {
    const session = availableSessions?.find(s => s?.id === sessionId);
    if (session) {
      if (confirm(`Join "${session?.title}" session? This will open a video conference.`)) {
        startVideoSession('jitsi');
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
        ⭐
      </span>
    ));
  };

  return (
    <div className="space-y-8">
      {/* Community Stats - Keep gradient with better contrast */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl text-white p-6">
        <h2 className="text-2xl font-bold mb-4">Peer Learning Community</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{communityStats?.activePeers}</div>
            <div className="text-sm text-green-100 font-medium">Active Peers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{communityStats?.totalSessions}</div>
            <div className="text-sm text-green-100 font-medium">Sessions Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{communityStats?.averageRating}</div>
            <div className="text-sm text-green-100 font-medium">Avg Rating</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{communityStats?.successfulMatches}</div>
            <div className="text-sm text-green-100 font-medium">Successful Matches</div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Keep existing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button 
          className="h-20 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" 
          fullWidth
          onClick={() => setShowHostModal(true)}
        >
          <div className="text-center">
            <Video className="w-6 h-6 mx-auto mb-1" />
            <div>Host a Session</div>
            <div className="text-xs opacity-90">Start video conference</div>
          </div>
        </Button>
        <Button variant="outline" className="h-20 text-lg" fullWidth>
          <div className="text-center">
            <Users className="w-6 h-6 mx-auto mb-1" />
            <div>Find Study Buddy</div>
          </div>
        </Button>
        <Button variant="outline" className="h-20 text-lg" fullWidth>
          <div className="text-center">
            <Calendar className="w-6 h-6 mx-auto mb-1" />
            <div>Schedule 1:1</div>
          </div>
        </Button>
      </div>

      {/* Video Conference Options - Enhanced colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900">
          <Video className="w-5 h-5 mr-2" />
          Quick Video Session
        </h3>
        <p className="text-gray-700 mb-4 font-medium">
          Start an instant video conference session for peer practice
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button 
            onClick={() => startVideoSession('jitsi')} 
            className="flex items-center justify-center space-x-2"
            disabled={isInSession}
          >
            <Video className="w-4 h-4" />
            <span>{isInSession ? 'Session Active' : 'Start Video Session'}</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => alert('Audio-only session feature coming soon!')}
            className="flex items-center justify-center space-x-2"
          >
            <Phone className="w-4 h-4" />
            <span>Audio Only</span>
          </Button>
        </div>
      </div>

      {/* Level Filter - Enhanced styling */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Filter by Experience Level</h3>
        <div className="flex flex-wrap gap-2">
          {['beginner', 'intermediate', 'advanced']?.map(level => (
            <button
              key={level}
              onClick={() => setUserLevel(level)}
              className={`
                px-4 py-2 rounded-lg capitalize font-semibold transition-all duration-200
                ${userLevel === level
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }
              `}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Available Sessions - Enhanced colors and contrast */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Available Practice Sessions</h3>
        
        <div className="space-y-4">
          {availableSessions?.map((session) => (
            <div 
              key={session?.id} 
              className="border-2 rounded-lg p-6 transition-all duration-200 hover:shadow-md bg-blue-50 border-blue-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">{getSessionTypeIcon(session?.sessionType)}</span>
                    <h4 className="text-lg font-semibold text-gray-900">{session?.title}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getLevelColor(session?.level)}`}>
                      {session?.level}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-700 mb-2">
                    <span className="font-medium">🎯 Host: {session?.host}</span>
                    <span className="font-medium">⏱️ {session?.duration}</span>
                    <span className="font-medium">📅 {session?.scheduledTime}</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {session?.topics?.map((topic, index) => (
                      <span key={index} className="px-2 py-1 bg-white/80 text-gray-800 rounded text-xs font-medium border border-gray-300">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="text-right ml-4">
                  <div className="text-sm text-gray-700 mb-2 font-medium">
                    {session?.participants}/{session?.maxParticipants} participants
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => joinSession(session?.id)}
                    disabled={session?.participants >= session?.maxParticipants}
                    className="flex items-center space-x-1"
                  >
                    <Video className="w-3 h-3" />
                    <span>{session?.participants >= session?.maxParticipants ? 'Full' : 'Join Session'}</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Your Upcoming Sessions - Enhanced colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Your Upcoming Sessions</h3>
        
        {upcomingBookings?.length > 0 ? (
          <div className="space-y-3">
            {upcomingBookings?.map((booking) => (
              <div key={booking?.id} className="flex items-center justify-between p-4 border border-gray-300 rounded-lg bg-white">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{getSessionTypeIcon(booking?.type)}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">{booking?.title}</h4>
                    <p className="text-sm text-gray-700 font-medium">
                      {booking?.date} at {booking?.time} with {booking?.host}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`
                    px-2 py-1 rounded text-xs font-semibold
                    ${booking?.status === 'confirmed' ? 'text-green-700 bg-green-100 border border-green-300' : 'text-orange-700 bg-orange-100 border border-orange-300'}
                  `}>
                    {booking?.status}
                  </span>
                  <Button size="sm" variant="outline">
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-600">
            <span className="text-4xl block mb-2">📅</span>
            <p className="font-medium">No upcoming sessions. Join one above to get started!</p>
          </div>
        )}
      </div>

      {/* Peer Feedback - Enhanced styling */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Recent Peer Feedback</h3>
        
        <div className="space-y-4">
          {[
            {
              session: "API Testing Workshop",
              peer: "Mike R.",
              rating: 5,
              feedback: "Great questions and very helpful explanations. Learned a lot about REST API testing!",
              date: "2 days ago"
            },
            {
              session: "Mock Interview",
              peer: "Sarah K.",
              rating: 4,
              feedback: "Good technical knowledge, but could work on communication clarity.",
              date: "1 week ago"
            }
          ]?.map((feedback, index) => (
            <div key={index} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-blue-900">{feedback?.session}</h4>
                <div className="flex items-center space-x-1">
                  {renderStars(feedback?.rating)}
                </div>
              </div>
              <p className="text-sm text-blue-800 mb-2 font-medium">"{feedback?.feedback}"</p>
              <div className="text-xs text-blue-700">
                From {feedback?.peer} • {feedback?.date}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Community Guidelines - Enhanced text colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Community Guidelines</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-green-800 mb-3">Best Practices</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Be respectful and constructive in feedback</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Come prepared with specific questions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Share knowledge and help others learn</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Maintain professional communication</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-red-800 mb-3">Community Rules</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5 font-bold">✗</span>
                <span className="text-gray-800">No spam or promotional content</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5 font-bold">✗</span>
                <span className="text-gray-800">Don't cancel last-minute without reason</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5 font-bold">✗</span>
                <span className="text-gray-800">No inappropriate or offensive behavior</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5 font-bold">✗</span>
                <span className="text-gray-800">Respect privacy and confidentiality</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Host Session Modal */}
      {showHostModal && <HostSessionModal />}
    </div>
  );
};

export default PeerPracticeSection;